import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { PlayContentMediaGrid } from './PlayContentMediaGrid';

type PlayContentPickerProps = {
  screenName: string;
  assets: MediaAsset[];
  mediaPending: boolean;
  mediaError: boolean;
  playingAssetId: string | null;
  playPending: boolean;
  error: string | null;
  /** 3 fits the Manage Content column; 2 for narrower surfaces. */
  columns?: 2 | 3;
  /** Library upload route that plays the finished upload on this display. */
  uploadPath?: string;
  onSelect: (assetId: string) => void;
  onCancel: () => void;
};

/** Compact choose-and-play — opens in the Manage Content column under what's live. */
export function PlayContentPicker({
  screenName,
  assets,
  mediaPending,
  mediaError,
  playingAssetId,
  playPending,
  error,
  columns = 3,
  uploadPath,
  onSelect,
  onCancel,
}: PlayContentPickerProps) {
  const { t } = useTranslation(['displays', 'common']);
  return (
    <section
      aria-labelledby="play-content-title"
      aria-describedby="play-content-desc"
      className="space-y-2 rounded-[10px] border border-line bg-panel2 px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 id="play-content-title" className="m-0 text-sm font-bold text-ink">
            {t('playContent.title', { name: screenName })}
          </h3>
          <p id="play-content-desc" className="m-0 mt-0.5 text-xs text-muted">
            {t('playContent.description')}
          </p>
        </div>
        <button
          type="button"
          disabled={playPending}
          onClick={onCancel}
          className="shrink-0 rounded-[8px] border border-line px-2.5 py-1.5 text-xs font-semibold text-ink hover:border-[var(--gs-accent)] disabled:opacity-50"
        >
          {t('common:actions.cancel')}
        </button>
      </div>

      {mediaPending ? (
        <p className="m-0 text-xs text-muted">{t('playContent.loading')}</p>
      ) : mediaError ? (
        <p className="m-0 text-xs" style={{ color: 'var(--gs-bad)' }} role="alert">
          {t('playContent.loadError')}
        </p>
      ) : (
        <PlayContentMediaGrid
          assets={assets}
          selectedId={playingAssetId}
          disabled={playPending}
          columns={columns}
          compact
          onSelect={onSelect}
        />
      )}

      {uploadPath && !mediaPending && !mediaError ? (
        <Link
          to={uploadPath}
          aria-disabled={playPending}
          className={[
            'inline-flex rounded-[8px] px-3 py-2 text-xs font-bold',
            assets.length === 0
              ? 'text-[var(--gs-accent-ink)]'
              : 'border border-line text-ink hover:border-[var(--gs-accent)]',
            playPending ? 'pointer-events-none opacity-50' : '',
          ].join(' ')}
          style={assets.length === 0 ? { background: 'var(--gs-accent-solid)' } : undefined}
        >
          {assets.length === 0 ? t('playContent.upload') : t('playContent.uploadNew')}
        </Link>
      ) : null}

      {playPending ? (
        <p className="m-0 text-xs font-medium text-muted" role="status">
          {t('playContent.putting')}
        </p>
      ) : null}

      {error ? (
        <p className="m-0 text-xs font-medium" style={{ color: 'var(--gs-bad)' }} role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
